import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { User as UserIcon, LogOut, CreditCard, Settings, Crown, Sparkles, HelpCircle, Sun, Moon, Palette } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';

const ProfileDropdown: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { user, logout } = useAuth();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
            navigate('/');
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

    const getInitials = (name: string, username: string) => {
        if (name) {
            const names = name.split(' ').filter(Boolean);
            if (names.length > 1) {
                return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
            }
            return names[0]?.[0]?.toUpperCase() || '';
        }
        if (username) {
            return username[0].toUpperCase();
        }
        return '';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)} 
                className="relative w-10 h-10 border border-slate-300 bg-white hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white rounded-full transition-colors"
            >
                {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Profile" className="w-full h-full rounded-full object-cover" />
                ) : (
                    <span className="font-bold text-sm">{getInitials(fullName, user?.username || '')}</span>
                )}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-white dark:border-slate-800"></div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        className="absolute right-0 mt-3 w-72 origin-top-right rounded-3xl bg-white/95 dark:bg-slate-800/95 backdrop-blur-lg border border-slate-200/80 dark:border-slate-600/30 shadow-2xl overflow-hidden z-[110]"
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
                        
                        {/* User Info Header */}
                        <div className="relative p-6 border-b border-slate-200 dark:border-slate-600/30">
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-xl flex items-center justify-center border border-slate-300 dark:border-slate-600/50">
                                        {user?.avatar_url ? (
                                            <img src={user.avatar_url} alt="Profile" className="w-full h-full rounded-xl object-cover" />
                                        ) : (
                                            <span className="font-bold text-lg text-slate-600 dark:text-slate-200">{getInitials(fullName, user?.username || '')}</span>
                                        )}
                                    </div>
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white dark:border-slate-800"></div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-slate-800 dark:text-slate-200 truncate">{fullName || user?.username}</p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* Menu Items */}
                        <div className="relative p-2">
                            <motion.button
                                onClick={() => { navigate('/app/profile'); setIsOpen(false); }}
                                className="flex items-center justify-between w-full px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-700/50 rounded-2xl transition-all duration-200 group"
                                whileHover={{ x: 5 }}
                            >
                                <div className="flex items-center">
                                    <div className="w-8 h-8 bg-slate-200/60 dark:bg-slate-700/50 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 rounded-xl flex items-center justify-center mr-3 transition-all duration-200">
                                        <UserIcon className="w-4 h-4" />
                                    </div>
                                    <span className="font-medium">Account Settings</span>
                                </div>
                                {user?.tier && (
                                    <span className="text-xs font-semibold px-2 py-1 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                                        {user.tier.display_name}
                                    </span>
                                )}
                            </motion.button>

                            <motion.button
                                onClick={() => { navigate('/pricing'); setIsOpen(false); }}
                                className="flex items-center w-full px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-700/50 rounded-2xl transition-all duration-200 group"
                                whileHover={{ x: 5 }}
                            >
                                <div className="w-8 h-8 bg-slate-200/60 dark:bg-slate-700/50 group-hover:bg-amber-100 dark:group-hover:bg-amber-500/20 rounded-xl flex items-center justify-center mr-3 transition-all duration-200">
                                    <Crown className="w-4 h-4" />
                                </div>
                                <span className="font-medium">Manage Plan</span>
                            </motion.button>

                            <motion.button
                                onClick={() => { navigate('/app/help'); setIsOpen(false); }}
                                className="flex items-center w-full px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/60 dark:hover:bg-slate-700/50 rounded-2xl transition-all duration-200 group"
                                whileHover={{ x: 5 }}
                            >
                                <div className="w-8 h-8 bg-slate-200/60 dark:bg-slate-700/50 group-hover:bg-blue-100 dark:group-hover:bg-blue-500/20 rounded-xl flex items-center justify-center mr-3 transition-all duration-200">
                                    <HelpCircle className="w-4 h-4" />
                                </div>
                                <span className="font-medium">Help & Support</span>
                            </motion.button>

                            {/* Theme Toggle - Mobile Only */}
                            <div className="sm:hidden flex items-center w-full px-4 py-3 text-sm text-slate-600 dark:text-slate-300 rounded-2xl">
                                <div className="w-8 h-8 bg-slate-200/60 dark:bg-slate-700/50 rounded-xl flex items-center justify-center mr-3">
                                    <Palette className="w-4 h-4" />
                                </div>
                                <span className="font-medium">Theme</span>
                                <div className="ml-auto flex items-center bg-slate-200/60 dark:bg-slate-700/50 rounded-lg p-1">
                                    <button
                                        onClick={() => setTheme('light')}
                                        className={`p-1.5 rounded-md transition-all duration-200 ${
                                            theme === 'light' 
                                                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm' 
                                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        <Sun className="w-3 h-3" />
                                    </button>
                                    <button
                                        onClick={() => setTheme('dark')}
                                        className={`p-1.5 rounded-md transition-all duration-200 ${
                                            theme === 'dark' 
                                                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm' 
                                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        <Moon className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>

                            <div className="my-2 h-px bg-slate-200 dark:bg-slate-600/30"></div>

                            <motion.button
                                onClick={handleLogout}
                                className="flex items-center w-full px-4 py-3 text-sm text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 rounded-2xl transition-all duration-200 group"
                                whileHover={{ x: 5 }}
                            >
                                <div className="w-8 h-8 bg-slate-200/60 dark:bg-slate-700/50 group-hover:bg-red-100 dark:group-hover:bg-red-500/20 rounded-xl flex items-center justify-center mr-3 transition-all duration-200">
                                    <LogOut className="w-4 h-4" />
                                </div>
                                <span className="font-medium">Sign Out</span>
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProfileDropdown;
